import React, { useState, useEffect, useMemo } from 'react';
import {   FaChartLine, FaRocket, FaFileAlt, FaDownload, FaArrowUp, FaArrowDown, FaStar,  FaExternalLinkAlt,  FaShieldAlt, FaExclamationTriangle,  FaSync, FaSearch, FaSortAmountDown, FaSortAmountUp, FaLock,FaSignInAlt,FaPhone,FaEnvelope,FaCrown,  FaCalendarAlt,  FaRupeeSign,  FaClipboardList,  FaLink,  FaPercentage} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import axios from 'axios';

const IPOResearch = () => {
  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [userData, setUserData] = useState(null);
  const [contentAccess, setContentAccess] = useState({ IPOResearch: 0 });


  // Page metadata========================================================
  const [pageMetadata, setPageMetadata] = useState({
    page_names: 'IPO Research & Analysis',
    description: 'Comprehensive IPO data from SEBI Registered Research Analyst'
  });
  // Add this fetch function (around line 60-80)
  const fetchPageMetadata = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/api/page-metadata/public/page2`);
      if (response.data) {
        setPageMetadata({
          page_names: response.data.page_name, // Using title from database
          description: response.data.description
        });
      }
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      // Keep default values if fetch fails
    }
  };

  // Add this inside your existing useEffect or create a new one (around line 100-110)
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.IPOResearch === 1) {
      fetchPageMetadata(); // Add this line
    }
  }, [isLoggedIn, checkingLogin, contentAccess.IPOResearch]);
  // Metadata End===========================================================


  
  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      const loggedIn = !!(accessToken && userDataStr);
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        const user = JSON.parse(userDataStr);
        setUserData(user);
        await fetchContentAccess(user.id);
      } else {
        setUserData(null);
        setContentAccess({ IPOResearch: 0 });
      }
      
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setCheckingLogin(false);
    }
  };
  
  const fetchContentAccess = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/content-access/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setContentAccess(response.data.data);
      } else {
        setContentAccess({ IPOResearch: 0 });
      }
    } catch (error) {
      console.error('Error fetching content access:', error);
      if (error.response?.status === 404) {
        setContentAccess({ IPOResearch: 0 });
      }
    }
  };
  
  useEffect(() => {
    checkLoginStatus();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  // ==================== END: LOGIN PROTECTION LOGIC ====================

  // ==================== IPO DATA FETCHING AND PROCESSING ====================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'DATE', direction: 'desc' }); // Default sort by DATE descending
  const [ipoHeaders, setIpoHeaders] = useState([]);
  const [processedData, setProcessedData] = useState({ 
    ipos: [], 
    disclosures: [], 
    ipoHeaders: [] 
  });

  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1y0E0k-BgoN_wzy31KPugIRnMPulFJjt05crlW8Iq6Yo/values/MM%20SEBI%20REGISTERED%20RA%20PLATFORM?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.IPOResearch === 1) {
      fetchIPOData();
    } else {
      if (isLoggedIn && !checkingLogin) {
        setLoading(false);
      }
    }
  }, [isLoggedIn, checkingLogin, contentAccess.IPOResearch]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.IPOResearch === 1) {
      const intervalId = setInterval(() => {
        fetchIPOData();
      }, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, checkingLogin, contentAccess.IPOResearch]);

  // Process data when data changes
  useEffect(() => {
    if (data && data.values) {
      const result = processData(data.values);
      setProcessedData(result);
      setIpoHeaders(result.ipoHeaders);
    }
  }, [data]);

  const fetchIPOData = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error('Failed to fetch IPO data');
      }
      
      const result = await response.json();
      setData(result);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching IPO data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Helper function to parse date from various formats
  const parseDate = (dateString) => {
    if (!dateString || dateString === '') return null;
    
    const cleaned = dateString.toString().trim();
    
    // Try to parse as DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
    let day, month, year;
    
    // Format: DD/MM/YYYY
    if (cleaned.includes('/')) {
      const parts = cleaned.split('/');
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        year = parseInt(parts[2]);
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) return date;
      }
    }
    
    // Format: DD-MM-YYYY
    if (cleaned.includes('-')) {
      const parts = cleaned.split('-');
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        year = parseInt(parts[2]);
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) return date;
      }
    }
    
    // Format: DD.MM.YYYY
    if (cleaned.includes('.')) {
      const parts = cleaned.split('.');
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        year = parseInt(parts[2]);
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) return date;
      }
    }
    
    // Try direct Date parsing
    const directDate = new Date(cleaned);
    if (!isNaN(directDate.getTime())) return directDate;
    
    return null;
  };

  // Process IPO data and disclosures
  const processData = (values) => {
    if (!values) {
      return { ipos: [], disclosures: [], ipoHeaders: [] };
    }
    
    let ipos = [];
    let disclosures = [];
    let ipoHeaders = [];
    let foundIPOTable = false;
    
    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;
      
      const rowString = row.join('').toUpperCase();
      const hasSno = rowString.includes('S.NO') || rowString.includes('SNO');
      const hasIPOName = rowString.includes('IPO NAME');
      
      // Process IPO table headers and data
      if (hasSno && hasIPOName && !foundIPOTable) {
        foundIPOTable = true;
        
        // Clean IPO headers
        ipoHeaders = row.map(h => {
          if (!h) return '';
          let cleaned = h.toString().trim();
          if (cleaned.split(' ').length > 3) {
            const words = cleaned.split(' ').filter(letter => letter.length > 0);
            const reconstructed = words.join('');
            if (reconstructed.toUpperCase().includes('IPONAME')) {
              return 'IPO NAME';
            } else if (reconstructed.toUpperCase().includes('CURRENTPRICE')) {
              return 'CURRENT PRICE';
            } else if (reconstructed.toUpperCase().includes('IPOPRICE')) {
              return 'IPO PRICE';
            } else if (reconstructed.toUpperCase().includes('LISTINGPRICE')) {
              return 'LISTING PRICE';
            } else if (reconstructed.toUpperCase().includes('LISTINGGAIN')) {
              return 'LISTING GAIN';
            } else if (reconstructed.toUpperCase().includes('CMP')) {
              return 'CMP';
            } else if (reconstructed.toUpperCase().includes('GAIN')) {
              return 'GAIN';
            } else if (reconstructed.toUpperCase().includes('RATING')) {
              return 'RATING';
            }
          }
          return cleaned;
        });
        
        // Process IPO data rows
        for (let j = i + 1; j < values.length; j++) {
          const ipoRow = values[j];
          if (!ipoRow || ipoRow.length === 0) continue;
          
          // Check if we've reached research report section
          const nextRowString = ipoRow.join('').toUpperCase();
          if (nextRowString.includes('MARKET MAESTROO RESERACH REPORT') || 
              nextRowString.includes('RESEARCH REPORT') ||
              (ipoRow[0] && ipoRow[0].toString().trim().includes('http'))) {
            break;
          }
          
          // Skip empty rows or rows with only S.NO
          const snoValue = ipoRow[2] || '';
          const sno = parseInt(snoValue.toString().trim());
          if (isNaN(sno) || sno < 1) continue;
          
          // Check if row has IPO name
          const ipoName = ipoRow[3] || ''; // IPO NAME is at index 3
          if (!ipoName || ipoName.toString().trim() === '') continue;
          
          // Check if row has any valid data
          const hasValidData = ipoRow.some((cell, idx) => {
            if (idx < ipoHeaders.length && ipoHeaders[idx]) {
              return cell !== undefined && cell !== null && cell.toString().trim() !== '';
            }
            return false;
          });
          
          if (!hasValidData) continue;
          
          // Create IPO object
          const ipoData = {};
          let hasData = false;
          
          ipoHeaders.forEach((header, index) => {
            if (header && ipoRow[index] !== undefined && ipoRow[index] !== null) {
              const value = ipoRow[index].toString().trim();
              if (value !== '') {
                ipoData[header] = value;
                hasData = true;
              }
            }
          });
          
          if (hasData) {
            // Calculate gain percentage if we have IPO PRICE and CURRENT PRICE/CMP
            const ipoPrice = parseFloat(ipoData['IPO PRICE']?.replace(/,/g, '')) || 0;
            const currentPrice = parseFloat(ipoData['CURRENT PRICE']?.replace(/,/g, '')) || 
                                parseFloat(ipoData['CMP']?.replace(/,/g, '')) || 0;
            const listingPrice = parseFloat(ipoData['LISTING PRICE']?.replace(/,/g, '')) || 0;
            
            let currentGain = 0;
            let listingGain = 0;
            
            if (ipoPrice > 0 && currentPrice > 0) {
              currentGain = ((currentPrice - ipoPrice) / ipoPrice) * 100;
            }
            
            if (ipoPrice > 0 && listingPrice > 0) {
              listingGain = ((listingPrice - ipoPrice) / ipoPrice) * 100;
            }
            
            // Check if "New" column exists and has value
            const isNew = ipoData['New'] && ipoData['New'].toString().trim().toLowerCase().includes('new');
            
            // Get date value for sorting
            const dateValue = ipoData['DATE'] || '';
            
            ipos.push({
              id: sno,
              data: ipoData,
              headers: Object.keys(ipoData),
              currentGain: currentGain,
              listingGain: listingGain,
              isPositive: currentGain > 0,
              isNew: isNew,
              dateValue: dateValue,
              originalRowIndex: j // Preserve original row index for stable ordering
            });
          }
        }
      }
      
      // Process disclosures only (ignore research reports, Google forms, etc.)
      if (row.length > 0 && row[0]) {
        const text = row[0].toString().trim();
        
        // Skip research reports and Google forms
        const lowerText = text.toLowerCase();
        if (lowerText.includes('market maestroo reseach report') ||
            lowerText.includes('research report') ||
            lowerText.includes('fill this google form') ||
            lowerText.includes('http') ||
            lowerText.includes('drive.google.com') ||
            lowerText.includes('forms.gle')) {
          continue;
        }
        
        if (text.includes('SEBI REGISTERED')) {
          disclosures.push({ 
            type: 'registration', 
            text: text.replace(/\r\n/g, '\n') 
          });
        } else if (text.includes('RISK DISCLOSURE')) {
          disclosures.push({ 
            type: 'risk', 
            text: text.replace(/\r\n/g, '\n') 
          });
        } else if (text.includes('DISCLAIMER') && !text.includes('GOOGLE FORM')) {
          disclosures.push({ 
            type: 'disclaimer', 
            text: text.replace(/\r\n/g, '\n') 
          });
        } else if (text.includes('INVESTOR CAUTION')) {
          disclosures.push({ 
            type: 'caution', 
            text: text.replace(/\r\n/g, '\n') 
          });
        }
      }
    }
    
    // Clean headers for display
    const cleanIPOHeaders = ipoHeaders
      .filter(h => h && h.trim() !== '')
      .map(h => normalizeHeaderName(h));
    
    return { 
      ipos, 
      disclosures, 
      ipoHeaders: cleanIPOHeaders 
    };
  };

  // Normalize header names
  const normalizeHeaderName = (header) => {
    if (!header) return '';
    
    let cleaned = header.trim();
    
    // If header has spaces between letters
    if (cleaned.split(' ').length > 3 && cleaned.replace(/\s+/g, '').length > 5) {
      const letters = cleaned.split(' ').filter(l => l.length > 0);
      const combined = letters.join('');
      
      // Check for common patterns
      const upperCombined = combined.toUpperCase();
      if (upperCombined.includes('IPONAME')) return 'IPO NAME';
      if (upperCombined.includes('CURRENTPRICE')) return 'CURRENT PRICE';
      if (upperCombined.includes('IPOPRICE')) return 'IPO PRICE';
      if (upperCombined.includes('LISTINGPRICE')) return 'LISTING PRICE';
      if (upperCombined.includes('LISTINGGAIN')) return 'LISTING GAIN';
      if (upperCombined.includes('GAIN')) return 'GAIN';
      if (upperCombined.includes('RATING')) return 'RATING';
      if (upperCombined.includes('CMP')) return 'CMP';
      if (upperCombined.includes('SNO') || upperCombined.includes('S.NO')) return 'S.NO';
      if (upperCombined.includes('DATE')) return 'DATE';
      if (upperCombined.includes('BOOKLET')) return 'BOOKLET';
      if (upperCombined.includes('LINK')) return 'LINK';
    }
    
    return cleaned;
  };

  // Format header names for display
  const formatHeaderName = (header) => {
    const normalized = normalizeHeaderName(header);
    
    // Common header mappings
    const headerMap = {
      'S.NO': 'S.No',
      'IPO NAME': 'IPO Name',
      'DATE': 'Date',
      'IPO PRICE': 'IPO Price',
      'CURRENT PRICE': 'Current Price',
      'LISTING PRICE': 'Listing Price',
      'LISTING GAIN': 'Listing Gain',
      'CMP': 'CMP',
      'GAIN': 'Gain',
      'RATING': 'Rating',
      'BOOKLET': 'Booklet',
      'LINK': 'Link',
      'TICKER': 'Ticker'
    };
    
    if (headerMap[normalized]) {
      return headerMap[normalized];
    }
    
    // Format other headers nicely
    return normalized
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\./g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Check if value is numeric
  const isNumeric = (value) => {
    if (typeof value === 'number') return true;
    if (typeof value !== 'string') return false;
    const clean = value.replace(/[₹%,]/g, '').trim();
    return !isNaN(clean) && !isNaN(parseFloat(clean));
  };

  // Format value for display
  const formatValue = (value, header = '') => {
    if (value === undefined || value === null || value === '') return 'N/A';
    
    const strValue = value.toString();
    const cleanValue = strValue.replace(/[₹%,]/g, '').trim();
    const isNum = !isNaN(cleanValue) && !isNaN(parseFloat(cleanValue));
    
    if (isNum) {
      const numValue = parseFloat(cleanValue);
      const headerUpper = normalizeHeaderName(header).toUpperCase();
      
      if (headerUpper.includes('GAIN') || headerUpper.includes('RETURN') || headerUpper.includes('YIELD')) {
        return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`;
      } else if (headerUpper.includes('PRICE') || headerUpper.includes('AMOUNT') || headerUpper.includes('VALUE')) {
        return `₹${numValue.toFixed(2)}`;
      } else if (numValue % 1 === 0) {
        return numValue.toString();
      } else {
        return numValue.toFixed(2);
      }
    }
    
    return strValue;
  };

  // Get filtered and sorted IPOs - with default sorting by DATE
  const filteredAndSortedIPOs = useMemo(() => {
    let filtered = [...processedData.ipos];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ipo => {
        const ipoData = ipo.data;
        return Object.values(ipoData).some(value => 
          value && value.toString().toLowerCase().includes(term)
        );
      });
    }
    
    // Apply sorting - default to DATE if no sort key is set
    const sortKey = sortConfig.key || 'DATE';
    const sortDirection = sortConfig.direction || 'desc';
    
    // Find the actual header name in the IPO data that matches our sort key
    let actualSortKey = null;
    if (sortKey === 'DATE') {
      actualSortKey = Object.keys(filtered[0]?.data || {}).find(key => 
        normalizeHeaderName(key).toUpperCase().includes('DATE')
      );
    } else {
      actualSortKey = Object.keys(filtered[0]?.data || {}).find(key => 
        normalizeHeaderName(key).toUpperCase() === normalizeHeaderName(sortKey).toUpperCase()
      );
    }
    
    if (actualSortKey) {
      filtered.sort((a, b) => {
        let aVal = a.data[actualSortKey] || '';
        let bVal = b.data[actualSortKey] || '';
        
        // Special handling for date fields
        if (sortKey === 'DATE') {
          const aDate = parseDate(aVal);
          const bDate = parseDate(bVal);
          
          if (aDate && bDate) {
            return sortDirection === 'desc' ? bDate - aDate : aDate - bDate;
          }
          // If one date is invalid, put valid dates first
          if (aDate && !bDate) return sortDirection === 'desc' ? -1 : 1;
          if (!aDate && bDate) return sortDirection === 'desc' ? 1 : -1;
          // If both invalid, fall back to string comparison
          const aStr = aVal.toString().toLowerCase();
          const bStr = bVal.toString().toLowerCase();
          if (aStr < bStr) return sortDirection === 'desc' ? 1 : -1;
          if (aStr > bStr) return sortDirection === 'desc' ? -1 : 1;
          return 0;
        }
        
        // Try numeric comparison for numeric fields
        const aNum = parseFloat(aVal.toString().replace(/[₹%,]/g, '')) || 0;
        const bNum = parseFloat(bVal.toString().replace(/[₹%,]/g, '')) || 0;
        
        if (aNum !== 0 || bNum !== 0) {
          if (aNum < bNum) return sortDirection === 'desc' ? 1 : -1;
          if (aNum > bNum) return sortDirection === 'desc' ? -1 : 1;
          return 0;
        }
        
        // Fall back to string comparison
        const aStr = aVal.toString().toLowerCase();
        const bStr = bVal.toString().toLowerCase();
        if (aStr < bStr) return sortDirection === 'desc' ? 1 : -1;
        if (aStr > bStr) return sortDirection === 'desc' ? -1 : 1;
        return 0;
      });
    } else {
      // If no matching sort key found, sort by original row index as fallback
      filtered.sort((a, b) => (a.originalRowIndex || 0) - (b.originalRowIndex || 0));
    }
    
    return filtered;
  }, [processedData.ipos, searchTerm, sortConfig]);

  // Calculate statistics
  const calculateStatistics = (ipos) => {
    const totalIPOs = ipos.length;
    const positiveGain = ipos.filter(s => s.currentGain > 0).length;
    const averageGain = totalIPOs > 0 
      ? (ipos.reduce((sum, s) => sum + s.currentGain, 0) / totalIPOs).toFixed(2)
      : 0;

    return {
      totalIPOs,
      positiveGain,
      averageGain
    };
  };

  const stats = useMemo(() => {
    return calculateStatistics(filteredAndSortedIPOs);
  }, [filteredAndSortedIPOs]);

  // Handle sort click
  const handleSort = (header) => {
    setSortConfig(prev => ({
      key: header,
      direction: prev.key === header && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Clear all filters and reset to default sorting
  const clearAllFilters = () => {
    setSearchTerm('');
    setSortConfig({ key: 'DATE', direction: 'desc' });
  };

  // Get available sortable headers
  const getSortableHeaders = () => {
    return ipoHeaders.filter(header => {
      const normalized = normalizeHeaderName(header).toUpperCase();
      return (
        normalized.includes('PRICE') ||
        normalized.includes('GAIN') ||
        normalized.includes('RATING') ||
        normalized.includes('S.NO') ||
        normalized.includes('SNO') ||
        normalized.includes('CMP') ||
        normalized.includes('DATE')
      );
    });
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  if (checkingLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access the IPO Research Dashboard.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <FaSignInAlt className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (contentAccess.IPOResearch !== 1) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Access Restricted</h2>
                <p className="text-gray-600 mb-3">
                  Hello <span className="font-semibold text-blue-600">{userData?.username}</span>! 
                  You don't have access to the IPO Research Dashboard yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This premium content requires special access permissions.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+919319745997"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaPhone className="text-lg" />
                    Call Us: +91 9319745997
                  </a>
                  <a 
                    href="mailto:support@marketmaestroo.com"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaEnvelope className="text-lg" />
                    Email: support@marketmaestroo.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading IPO Research Dashboard...</p>
          <p className="text-sm text-gray-500 mt-2">Auto-refresh every 60 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchIPOData}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sortableHeaders = getSortableHeaders();

  return (
    <>
      <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">{pageMetadata.page_names}</h1>
                  <p className="text-blue-100">{pageMetadata.description}</p>
                  <p className="text-blue-200 text-sm mt-1">SEBI Registration No: INH000021757</p>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                  <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaShieldAlt className="text-yellow-300" />
                    <span className="font-semibold">SEBI INH000021757</span>
                  </div>
                  <button
                    onClick={fetchIPOData}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                  >
                    <FaSync className="text-sm" />
                    Refresh Now
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Search and Sort Controls */}
            <div className="mb-8">
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search IPOs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-3 rounded-lg transition-colors flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                >
                  <FaSync className="text-sm" />
                  Clear Filters
                </button>
              </div>

              {/* Sort Controls */}
              <div className="flex items-center gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex-wrap">
                <span className="text-gray-600 font-medium whitespace-nowrap">Sort by:</span>
                <div className="flex gap-2 flex-wrap">
                  {sortableHeaders.map(header => (
                    <button
                      key={header}
                      onClick={() => handleSort(header)}
                      className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap ${
                        sortConfig.key === header 
                          ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                      }`}
                    >
                      {formatHeaderName(header)}
                      {sortConfig.key === header && (
                        sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                      )}
                    </button>
                  ))}
                </div>
                {/* {sortConfig.key && (
                  <div className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                    Sorted by: {formatHeaderName(sortConfig.key)} {sortConfig.direction === 'desc' ? '(Newest First)' : '(Oldest First)'}
                  </div>
                )} */}
                {/* <div className="ml-auto text-sm text-gray-500 whitespace-nowrap">
                  {filteredAndSortedIPOs.length} IPOs • {ipoHeaders.length} columns
                </div> */}
              </div>
            </div>

            {filteredAndSortedIPOs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-blue-200">
                <FaRocket className="text-blue-400 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No IPO Data Available</h3>
                <p className="text-gray-500">Try adjusting your search criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredAndSortedIPOs.map((ipo, index) => {
                  const ipoData = ipo.data;
                  const headers = Object.keys(ipoData);
                  
                  // Get key fields
                  const ipoNameHeader = headers.find(h => 
                    normalizeHeaderName(h).toUpperCase().includes('IPO') && 
                    normalizeHeaderName(h).toUpperCase().includes('NAME')
                  );
                  const tickerHeader = headers.find(h => 
                    normalizeHeaderName(h).toUpperCase().includes('TICKER')
                  );
                  const ratingHeader = headers.find(h => 
                    normalizeHeaderName(h).toUpperCase().includes('RATING')
                  );
                  const dateHeader = headers.find(h => 
                    normalizeHeaderName(h).toUpperCase().includes('DATE')
                  );
                  
                  const ipoName = ipoNameHeader ? ipoData[ipoNameHeader] : '';
                  const ticker = tickerHeader ? ipoData[tickerHeader] : '';
                  const rating = ratingHeader ? ipoData[ratingHeader] : '';
                  const date = dateHeader ? ipoData[dateHeader] : '';
                  
                  // Parse rating for badge color
                  let ratingValue = 0;
                  if (rating) {
                    const ratingParts = rating.toString().split('/');
                    if (ratingParts.length === 2) {
                      ratingValue = parseFloat(ratingParts[0]) || 0;
                    }
                  }
                  
                  return (
                    <motion.div
                      key={ipo.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ y: -4 }}
                      className="group relative"
                    >
                      {/* New Badge - Display as diagonal ribbon in top right corner (matching RegisteredRAPlateform style) */}
                      {ipo.isNew && (
                        <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24">
                          <div 
                            className="absolute top-10 transform -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold py-1 px-6 shadow-lg"
                            style={{ right: "-57px", top: "18px" }}
                          >
                            NEW
                          </div>
                        </div>
                      )}

                      <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl border border-blue-200 transition-all duration-300 overflow-hidden hover:border-blue-300 hover:shadow-xl h-full relative">
                        
                        {/* Rating Badge - Top Right */}
                        {rating && (
                          <div className="absolute top-4 right-4 z-10">
                            <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg ${
                              ratingValue >= 8 ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' :
                              ratingValue >= 6 ? 'bg-blue-500 text-white' :
                              ratingValue >= 4 ? 'bg-amber-500 text-white' :
                              'bg-red-500 text-white'
                            }`}>
                              <FaStar className="text-xs" />
                              {rating}
                            </div>
                          </div>
                        )}

                        <div className="p-5">
                          {/* IPO Header */}
                          <div className="mb-4">
                            {ipoName && (
                              <h3 className="font-bold text-gray-900 text-lg line-clamp-2 group-hover:text-blue-600 transition-colors mb-1 pr-16">
                                {ipoName}
                              </h3>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {ticker && ticker !== '-' && (
                                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                                  {ticker}
                                </span>
                              )}
                              {date && (
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded flex items-center gap-1">
                                  <FaCalendarAlt className="text-xs" />
                                  {date}
                                </span>
                              )}
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                ID: {ipo.id}
                              </span>
                            </div>
                          </div>

                          {/* Dynamic IPO Data Grid */}
                          <div className="space-y-3">
                            {headers.map(header => {
                              const normalizedHeader = normalizeHeaderName(header);
                              const headerUpper = normalizedHeader.toUpperCase();
                              const value = ipoData[header];
                              
                              // Skip header rows already shown and skip "New" column from data display
                              if (headerUpper.includes('IPO') && headerUpper.includes('NAME')) return null;
                              if (headerUpper.includes('TICKER')) return null;
                              if (headerUpper.includes('S.NO') || headerUpper.includes('SNO')) return null;
                              if (headerUpper.includes('RATING')) return null;
                              if (headerUpper.includes('DATE')) return null;
                              if (headerUpper === 'NEW') return null; // Skip "New" column from data display
                              if (!value || value.trim() === '') return null;
                              
                              // Special styling for different fields
                              const isGain = headerUpper.includes('GAIN');
                              const isPrice = headerUpper.includes('PRICE') || headerUpper.includes('CMP');
                              const isBooklet = headerUpper.includes('BOOKLET');
                              const isLink = headerUpper.includes('LINK');
                              
                              // Skip booklet and link for main grid (they get special buttons)
                              if (isBooklet || isLink) return null;
                              
                              let bgColor = 'bg-gray-50';
                              let textColor = 'text-gray-900';
                              
                              if (isGain) {
                                // Parse gain value to determine if positive or negative
                                const gainStr = value.toString().trim();
                                const hasMinusSign = gainStr.includes('-');
                                const hasPlusSign = gainStr.includes('+');
                                
                                // Clean to extract numeric value
                                const cleanStr = gainStr.replace(/[+%-]/g, '');
                                const numValue = parseFloat(cleanStr) || 0;
                                
                                // Determine if positive or negative
                                const isPositiveGain = (!hasMinusSign && numValue > 0) || hasPlusSign;
                                const isNegativeGain = hasMinusSign || (numValue < 0 && !hasPlusSign);
                                
                                if (isPositiveGain) {
                                  bgColor = 'bg-green-50';
                                  textColor = 'text-green-600';
                                } else if (isNegativeGain) {
                                  bgColor = 'bg-red-50';
                                  textColor = 'text-red-600';
                                } else {
                                  bgColor = 'bg-gray-50';
                                  textColor = 'text-gray-600';
                                }
                              } else if (isPrice) {
                                bgColor = 'bg-blue-50';
                                textColor = 'text-blue-700';
                              }
                              
                              return (
                                <div key={header} className={`p-3 rounded-lg ${bgColor}`}>
                                  <div className="flex justify-between items-center">
                                    <div className="text-xs font-medium text-gray-600">
                                      {formatHeaderName(header)}
                                    </div>
                                    <div className={`font-bold ${textColor} flex items-center gap-1`}>
                                      {isGain && (
                                        <>
                                          {value.toString().includes('+') && <FaArrowUp className="text-sm" />}
                                          {value.toString().includes('-') && <FaArrowDown className="text-sm" />}
                                        </>
                                      )}
                                      {isPrice && <FaRupeeSign className="text-xs mr-0.5" />}
                                      {formatValue(value, header)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Action Buttons for Booklet and Link */}
                          <div className="mt-4 flex gap-2">
                            {ipoData['BOOKLET'] && ipoData['BOOKLET'].includes('http') && (
                              <a
                                href={ipoData['BOOKLET']}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                              >
                                <FaFileAlt />
                                Booklet
                              </a>
                            )}
                            {ipoData['LINK'] && ipoData['LINK'].includes('http') && (
                              <a
                                href={ipoData['LINK']}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                              >
                                <FaExternalLinkAlt />
                                Link
                              </a>
                            )}
                          </div>

                        
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Disclosures Section - ALWAYS show if available */}
            {processedData.disclosures.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-12"
              >
                <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <FaShieldAlt className="text-blue-500" />
                    Regulatory Disclosures
                  </h3>
                  
                  <div className="space-y-6">
                    {processedData.disclosures.map((disclosure, index) => {
                      let title = '';
                      let icon = null;
                      let color = 'text-gray-700';
                      
                      if (disclosure.type === 'registration') {
                        title = 'SEBI Registration';
                        icon = <FaShieldAlt className="text-blue-500" />;
                        color = 'text-blue-700';
                      } else if (disclosure.type === 'risk') {
                        title = 'Risk Disclosure';
                        icon = <FaExclamationTriangle className="text-amber-500" />;
                        color = 'text-amber-700';
                      } else if (disclosure.type === 'disclaimer') {
                        title = 'Disclaimer';
                        icon = <FaExclamationTriangle className="text-gray-500" />;
                        color = 'text-gray-700';
                      } else if (disclosure.type === 'caution') {
                        title = 'Investor Caution';
                        icon = <FaExclamationTriangle className="text-red-500" />;
                        color = 'text-red-700';
                      }
                      
                      return (
                        <div key={index} className="bg-white p-5 rounded-xl border border-gray-200">
                          <div className="flex items-start gap-3 mb-3">
                            {icon}
                            <h4 className={`font-bold ${color}`}>{title}</h4>
                          </div>
                          <p className="text-gray-600 whitespace-pre-line text-sm leading-relaxed">
                            {disclosure.text}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Footer */}
            <div className="mt-8 text-center text-sm text-gray-500">
              {lastUpdated && (
                <p className="mt-1">Last refreshed: {lastUpdated.toLocaleString()}</p>
              )}
              <p className="mt-2 text-xs bg-gray-100 inline-block px-3 py-1 rounded-full">
                Market Maestroo Private Limited | SEBI Registration No: INH000021757 | Principal Officer: Kritika Yadav
              </p>
            </div>
          </div>
        </div>
      <Footer />
    </>
  );
};

export default IPOResearch;